/**
 * Attendance Page - Main container component
 * Orchestrates all attendance-related components and state
 */

import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/_core/hooks/usePermissions";

// Components
import { AttendanceFilters } from "./components/AttendanceFilters";
import { AttendanceSummary } from "./components/AttendanceSummary";
import { AttendanceSummaryTable } from "./components/AttendanceSummaryTable";
import { AttendanceTable } from "./components/AttendanceTable";
import { AttendanceDialogWithServices } from "./components/AttendanceDialogWithServices";
import { AttendancePrint } from "./components/AttendancePrint";
import { IncomePreferencesPanel } from "./components/IncomePreferencesPanel";

// Hooks
import { useAttendanceDataWithServices } from "./hooks/useAttendanceDataWithServices";
import { useAttendancePrint } from "./hooks/useAttendancePrint";

// Types
import type {
  DochadzkaWithServices,
  AttendanceFormDataWithServices,
} from "./types";

export default function AttendancePage() {
  const { t } = useTranslation();
  const {
    canWriteAttendance,
    canListAllClients,
    canViewMonthlyAttendanceSummary,
  } = usePermissions();

  // Helper function to get current month date range (useMemo to run only once)
  const getCurrentMonthDateRange = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    // First day of current month
    const firstDay = new Date(year, month, 1);
    // Last day of current month
    const lastDay = new Date(year, month + 1, 0);

    // Format to YYYY-MM-DD without timezone issues
    const format = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    return {
      firstDay: format(firstDay),
      lastDay: format(lastDay),
    };
  }, []); // Empty dependency array means it only runs once

  // State
  const [selectedKlientId, setSelectedKlientId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(getCurrentMonthDateRange.firstDay);
  const [endDate, setEndDate] = useState(getCurrentMonthDateRange.lastDay);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDochadzka, setEditingDochadzka] =
    useState<DochadzkaWithServices | null>(null);
  const [isSummaryMode, setIsSummaryMode] = useState(false);

  // Custom hooks
  const {
    klienti,
    sortedDochadzka,
    allDochadzka,
    summary,
    isLoading,
    createAttendance,
    updateAttendance,
    deleteAttendance,
  } = useAttendanceDataWithServices({
    selectedKlientId,
    startDate,
    endDate,
  });

  const {
    printSettings,
    setPrintSettings,
    toggleAllColumns,
    areAllColumnsSelected,
    handlePrint,
  } = useAttendancePrint();

  const utils = trpc.useUtils();
  const { data: additionalServices = [] } = trpc.doplnkoveSluzby.list.useQuery(
    undefined,
    {
      enabled: canViewMonthlyAttendanceSummary,
    }
  );
  const addonCategories = useMemo(
    () =>
      Array.from(
        new Set(additionalServices.map(service => service.kategoria))
      ).sort(),
    [additionalServices]
  );

  useEffect(() => {
    if (klienti?.length === 1 && selectedKlientId !== klienti[0].id) {
      setSelectedKlientId(klienti[0].id);
    }
  }, [klienti, selectedKlientId]);

  // Handlers
  const handleOpenDialog = () => {
    setEditingDochadzka(null);
    setIsDialogOpen(true);
  };

  const handleEdit = async (session: DochadzkaWithServices) => {
    // Fetch full session data with services
    try {
      const fullSession = await utils.dochadzka.getWithServices.fetch({
        id: session.id,
      });
      setEditingDochadzka(fullSession);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching session with services:", error);
      // Fallback to basic session data
      setEditingDochadzka(session);
      setIsDialogOpen(true);
    }
  };

  const handleSubmit = async (data: AttendanceFormDataWithServices) => {
    if (editingDochadzka) {
      return await updateAttendance(editingDochadzka.id, data);
    } else {
      return await createAttendance(data);
    }
  };

  const handleDelete = async (id: number) => {
    await deleteAttendance(id);
  };

  const handlePrintAllClients = async () => {
    if (!canListAllClients) return;
    // Fetch all clients data
    await utils.dochadzka.getByDateRange.invalidate();
    const allData = await utils.dochadzka.getByDateRange.fetch({
      startDate,
      endDate,
      klientId: undefined,
    });

    const clientInfo = t("attendance:print.allClients");
    handlePrint(allData, clientInfo, startDate, endDate, true, klienti);
  };

  const handlePrintSelectedClient = () => {
    const selectedClient = klienti?.find(k => k.id === selectedKlientId);
    if (!selectedClient) return;

    const clientInfo = `${selectedClient.alias} - ${selectedClient.meno} ${selectedClient.priezvisko}`;
    handlePrint(
      sortedDochadzka,
      clientInfo,
      startDate,
      endDate,
      false,
      klienti
    );
  };

  return (
    <DashboardLayout>
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t("attendance:title")}</h1>
            <p className="text-muted-foreground">{t("attendance:subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <AttendancePrint
              selectedKlientId={selectedKlientId}
              klienti={klienti}
              printSettings={printSettings}
              onPrintSettingsChange={setPrintSettings}
              areAllColumnsSelected={areAllColumnsSelected}
              onToggleAllColumns={toggleAllColumns}
              onPrintAllClients={
                canListAllClients ? handlePrintAllClients : undefined
              }
              onPrintSelectedClient={handlePrintSelectedClient}
            />
            {canWriteAttendance && (
              <Button onClick={handleOpenDialog}>
                <Plus className="h-4 w-4 mr-2" />
                {t("attendance:addSession")}
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <AttendanceFilters
          selectedKlientId={selectedKlientId}
          onKlientChange={setSelectedKlientId}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          klienti={klienti}
          isSummaryMode={isSummaryMode}
          onSummaryModeChange={setIsSummaryMode}
          allowAllClients={canListAllClients}
        />

        {canViewMonthlyAttendanceSummary && (
          <IncomePreferencesPanel addonCategories={addonCategories} />
        )}

        {/* Summary Cards */}
        <AttendanceSummary
          totalSessions={sortedDochadzka.length}
          totalDuration={summary.totalDuration}
          totalPrice={summary.totalPrice}
          totalProfit={summary.totalProfit}
        />

        {/* Summary Table - Only shown when in summary mode */}
        {isSummaryMode && (
          <AttendanceSummaryTable
            data={sortedDochadzka}
            klienti={klienti}
            isLoading={isLoading}
          />
        )}

        {/* Attendance Table - Shown when not in summary mode */}
        {!isSummaryMode && (
          <AttendanceTable
            data={sortedDochadzka}
            klienti={klienti}
            isLoading={isLoading}
            onEdit={canWriteAttendance ? handleEdit : undefined}
            onDelete={canWriteAttendance ? handleDelete : undefined}
          />
        )}

        {/* Add/Edit Dialog with Services */}
        <AttendanceDialogWithServices
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          editingSession={editingDochadzka}
          selectedKlientId={selectedKlientId}
          klienti={klienti}
          onSubmit={handleSubmit}
        />
      </div>
    </DashboardLayout>
  );
}
